<template>
  <section>
    <component
      :is="currentTabComponent"
      :role-id="currentRoleId"
      :read-only="readOnly"
      :use-data-source="true"
      :role-data="getRoleData"
      @saveRoleData="saveRoleData"
      @deleteRoleData="handleDeleteRole"
      ref="componentRef"
    />
  </section>
</template>

<script lang="ts" setup>
  import { computed, shallowRef, toRefs } from 'vue';
  import RoleTab from './RoleTab.vue';
  import { useRoleStore } from '../role-store';
  import { RoleData } from '../role-data.interface';
  import AccessGuard from '@/app/shared/guards/access-guard';

  interface RoleDetailEvents {
    (_event: 'roleCreated'): void;
    (_event: 'closeCurrentTab', _params: number): void;
    (_event: 'deleteById', _params: number | string): void;
  }

  interface RoleDetailProps {
    currentRoleId: number;
  }

  const currentTabComponent = shallowRef(RoleTab);
  const props = defineProps<RoleDetailProps>();
  const emit = defineEmits<RoleDetailEvents>();
  const roleStore = useRoleStore();
  const { currentRoleId } = toRefs(props);

  const getRoleData = computed(() => {
    const roleDetail = roleStore.getRoleById(currentRoleId.value);
    const roleData = roleDetail?.roleData;
    return {
      id: roleData?.id || -1,
      name: roleData?.name || '',
      clientId: roleData?.clientId || 0,
      ssoDefaultRole: roleData?.ssoDefaultRole || false,
      accessRights: roleData?.accessRights || [],
    };
  });

  const readOnly = computed(() => !AccessGuard.canModify('ROLE'));

  const saveRoleData = (roleData: RoleData) => {
    if (roleData.id == -1) {
      saveData(roleData);
    } else {
      updateData(roleData);
    }
  };

  const saveData = async (roleData: RoleData) => {
    try {
      await roleStore.createRole(roleData);
      emit('closeCurrentTab', currentRoleId.value);
    } catch (error) {
      console.error(error);
    }
  };

  const updateData = async (roleData: RoleData) => {
    try {
      await roleStore.updateRole(currentRoleId.value, roleData);
      emit('closeCurrentTab', currentRoleId.value);
    } catch (error) {
      console.error(error);
    }
  };

  const handleDeleteRole = (roleId: number | string) => {
    emit('deleteById', roleId);
  };
</script>
